import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MagnifyingGlassIcon, ClockIcon, FireIcon } from '@heroicons/react/24/outline';
import { FaWrench, FaHome, FaLaptopCode, FaTruck, FaSpa, FaChalkboardTeacher } from 'react-icons/fa';

// Import de l'image de fond
import heroBg from '../../assets/images/vadom.jpeg';

const categories = [
  { id: 'artisanat', name: 'Artisanat & Réparations', icon: FaWrench },
  { id: 'domicile', name: 'Services à domicile', icon: FaHome },
  { id: 'numerique', name: 'Services numériques', icon: FaLaptopCode },
  { id: 'transport', name: 'Livraison & Transport', icon: FaTruck },
  { id: 'beaute', name: 'Beauté & Bien-être', icon: FaSpa },
  { id: 'formation', name: 'Formation & Coaching', icon: FaChalkboardTeacher },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('artisanat');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchRef = useRef(null);

  // Charger les recherches récentes depuis localStorage
  useEffect(() => {
    const saved = localStorage.getItem('vadom_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Sauvegarder une recherche
  const saveRecentSearch = (term) => {
    if (!term.trim()) return;
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('vadom_recent_searches', JSON.stringify(updated));
  };

  // Rechercher un service
  const performSearch = (query) => {
    if (!query.trim()) return;
    saveRecentSearch(query);
    navigate(`/services?search=${encodeURIComponent(query)}`);
    setShowSuggestions(false);
  };

  // Recherches fréquentes (exemple statique)
  const frequentSearches = ['Plomberie', 'Ménage', 'Électricité', 'Développement web', 'Coiffure'];

  // Fermer les suggestions au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const subServices = {
    artisanat: ['Plomberie', 'Électricité', 'Menuiserie', 'Maçonnerie', 'Peinture'],
    domicile: ['Ménage', 'Jardinage', "Garde d'enfants", 'Cuisine', 'Repassage'],
    numerique: ['Design', 'Développement web', 'Marketing digital', 'Community management'],
    transport: ['Livraison express', 'Déménagement', 'Chauffeur', 'Courses'],
    beaute: ['Coiffure', 'Maquillage', 'Manucure', 'Massage', 'Soins visage'],
    formation: ['Cours langues', 'Coaching', 'Informatique', 'Soutien scolaire'],
  };

  return (
    <section className="relative bg-[#111111] overflow-hidden min-h-[90vh] flex items-center">
      {/* Image de fond avec opacité */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${heroBg})`,
          opacity: 0.12
        }}
      />
      
      {/* Overlay sombre */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/70 via-[#111111]/50 to-[#111111]/70" />
      
      {/* Cercles décoratifs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/15 rounded-full -translate-y-1/2 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#F97316]/15 rounded-full translate-y-1/4 -translate-x-1/4" />
      
      <div className="container-custom relative py-20 lg:py-28 z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <h1 className="text-4xl lg:text-5xl xxl:text-6xl font-display font-bold mb-5">
            <span className="text-[#F97316]">VADOM</span> <br /> connecte instantanément
            <span className="text-white"> artisans, techniciens, livreurs</span>
          </h1>
          <p className="text-lg lg:text-xl text-[#AAAAAA] mb-8">
            Dans une interface sécurisée pensée pour l'Afrique de l'Ouest.
          </p>
        </motion.div>

        {/* Barre de recherche avec suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-2xl mx-auto mb-12 relative"
          ref={searchRef}
        >
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#AAAAAA]" />
            <input
              type="text"
              placeholder="Que voulez-vous faire ? Ex: Plomberie, Nettoyage..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={(e) => e.key === 'Enter' && performSearch(searchTerm)}
              className="w-full pl-12 pr-4 py-4 rounded-full bg-[#252525]/80 text-white 
                         placeholder-[#AAAAAA] border border-[#333333]
                         focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent
                         shadow-lg backdrop-blur-sm"
            />
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] rounded-xl border border-[#333333] shadow-xl overflow-hidden z-20">
              {/* Recherches récentes */}
              {recentSearches.length > 0 && (
                <div className="p-3 border-b border-[#252525]">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <ClockIcon className="h-3 w-3" />
                    <span>Récentes</span>
                  </div>
                  {recentSearches.map((term, idx) => (
                    <button
                      key={idx}
                      onClick={() => performSearch(term)}
                      className="w-full text-left px-3 py-2 text-[#AAAAAA] hover:bg-[#252525] rounded-lg transition flex items-center gap-2"
                    >
                      <ClockIcon className="h-4 w-4 text-gray-600" />
                      {term}
                    </button>
                  ))}
                </div>
              )}

              {/* Recherches fréquentes */}
              <div className="p-3">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <FireIcon className="h-3 w-3 text-[#F97316]" />
                  <span>Recherches fréquentes</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {frequentSearches.map((term) => (
                    <button
                      key={term}
                      onClick={() => performSearch(term)}
                      className="px-3 py-1.5 bg-[#252525] text-[#AAAAAA] rounded-full text-sm hover:bg-[#F97316] hover:text-white transition"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Catégories */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          {categories.map((cat) => {
            const IconComponent = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#F97316] text-white shadow-vadom'
                    : 'bg-[#252525]/70 text-[#AAAAAA] hover:bg-[#333333] backdrop-blur-sm'
                }`}
              >
                <IconComponent className="text-sm" />
                <span>{cat.name.split(' & ')[0]}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Sous-services */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap justify-center gap-2"
        >
          {subServices[activeCategory]?.map((service) => (
            <button
              key={service}
              onClick={() => performSearch(service)}
              className="px-4 py-2 bg-[#252525]/70 rounded-full text-sm text-[#AAAAAA] 
                         hover:text-[#F97316] hover:bg-[#333333] border border-[#333333]
                         transition-all backdrop-blur-sm cursor-pointer"
            >
              {service}
            </button>
          ))}
        </motion.div>

        {/* Description de la catégorie */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-[#AAAAAA] mt-6"
        >
          Trouvez l'artisan qualifié pour chaque besoin
        </motion.p>
      </div>

      {/* Wave SVG en bas */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
                fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;